import { useRouter } from 'next/router'
import { useEffect } from 'react'

import { createParam } from 'solito'

import useCurrencyState from 'app/hooks/useCurrencyState'
import usePaginationForm from 'app/hooks/usePaginationForm'
import useSearchForm from 'app/hooks/useSearchForm'
import { useGetListingsByUser } from 'app/services/Query/listing/listing.query'
import { useGetUserBySlug } from 'app/services/Query/user/user.query'
import useAuthStore, {
  getAuthToken,
  getAuthUserId,
  getAuthUserRole,
  getIsInitialized
} from 'app/store/auth.store'
import { USER_ROLE } from 'app/utils/constants/role.constants'

const { useParam } = createParam()

export const useProductView = () => {
  const { currency } = useCurrencyState()
  const { isFallback } = useRouter()
  const [slug = ''] = useParam('slug')
  const userId = useAuthStore(getAuthUserId)
  const token = useAuthStore(getAuthToken)
  const isInitialized = useAuthStore(getIsInitialized)
  const role = useAuthStore(getAuthUserRole)
  const isBusinessUser = role === USER_ROLE.BUSINESS

  const { data: userBySlug } = useGetUserBySlug({
    slug,
    options: { enabled: !!userId }
  })

  const path = userBySlug?.available_listings ? `/store/${slug}/products` : ''
  const { methods, values } = useSearchForm<UserListingsParams>({
    path
  })

  const { page: paginationPage, setPage } = usePaginationForm({
    control: methods?.control,
    ...(userBySlug?.available_listings && { setValue: methods?.setValue })
  })

  const { p, ...reqValues } = values as { p: number }

  const params = {
    page: Number(p) || 1,
    slug,
    ...reqValues
  }
  const enabled = !!token ? !!userId && !!slug : !!slug

  const { data, isFetching, isFetchedAfterMount, isFetched, refetch } =
    useGetListingsByUser({
      options: {
        enabled,
        placeholderData: {
          listings: [{ id: '1' }, { id: '2' }, { id: '3' }, { id: '4' }],
          pagy: {},
          listings_count: 0
        } as PaginationListingModel
      },
      params
    })

  const isAuthLoading = !!token && !isInitialized
  const isListingsLoading = !isFetchedAfterMount && isFetching
  const isPageLoading = isFallback || isAuthLoading || isListingsLoading

  const { listings, pagy, part_numbers } =
    data || ({} as PaginationListingModel)

  const isEmpty = !listings?.length && !isFetching

  const pages = pagy?.pages || 1
  const showPagination = isFetched && pages > 1

  useEffect(() => {
    if (currency && enabled) {
      refetch()
    }
  }, [currency])

  return {
    isPageLoading,
    isEmpty,
    showPagination,
    listings,
    pagy,
    part_numbers,
    setPage,
    paginationPage,
    methods,
    userBySlug,
    pages,
    isBusinessUser
  }
}
